import { useState, useEffect, useMemo } from 'react';
import { useCallData } from '../contexts/CallDataContext';
import { useTickets } from '../contexts/TicketContext';
import { useEquipment } from '../contexts/EquipmentContext';
import { useCustomer } from '../contexts/CustomerContext';
import { useDataAccess } from './useDataAccess';
import { searchCalls, searchOrders, searchTickets, searchInventory, searchShopifyOrders } from '../utils/searchUtils';

export const useSearchResults = (searchQuery) => {
  const { data: callData, loading: callLoading, error: callError } = useCallData();
  const { tickets, isLoading: ticketsLoading, error: ticketsError } = useTickets();
  const { equipmentData, loading: equipmentLoading, error: equipmentError, isWardsCustomer } = useEquipment();
  const { getEffectiveCustomerId } = useCustomer();
  const { makeCustomerApiRequest } = useDataAccess();
  
  const [shopifyOrders, setShopifyOrders] = useState([]);
  const [shopifyOrdersLoading, setShopifyOrdersLoading] = useState(false);
  const [shopifyOrdersError, setShopifyOrdersError] = useState(null);
  
  const [searchResults, setSearchResults] = useState({
    calls: [],
    orders: [],
    shopifyOrders: [],
    tickets: [],
    inventory: []
  });

  // Fetch Shopify orders for kronk customer
  useEffect(() => {
    const customerId = getEffectiveCustomerId();
    if (customerId === 'kronk') {
      const fetchShopifyOrders = async () => {
        setShopifyOrdersLoading(true);
        setShopifyOrdersError(null);
        try {
          const params = new URLSearchParams();
          params.set('limit', '250');
          const endpoint = `orders?${params.toString()}`;
          const response = await makeCustomerApiRequest(endpoint, {}, 'kronk');
          if (response && response.orders) {
            setShopifyOrders(response.orders);
          } else {
            setShopifyOrders([]);
          }
        } catch (err) {
          console.error('Failed to fetch Shopify orders:', err);
          setShopifyOrdersError(err.message || 'Failed to load orders');
          setShopifyOrders([]);
        } finally {
          setShopifyOrdersLoading(false);
        }
      };
      fetchShopifyOrders();
    } else {
      setShopifyOrders([]);
    }
  }, [getEffectiveCustomerId, makeCustomerApiRequest]);

  // Single effect to handle all search logic
  useEffect(() => {
    // Always reset when query is empty
    if (!searchQuery || searchQuery.trim() === '') {
      setSearchResults({
        calls: [],
        orders: [],
        shopifyOrders: [],
        tickets: [],
        inventory: []
      });
      return;
    }

    const customerId = getEffectiveCustomerId();
    const isKronk = customerId === 'kronk';

    // Perform search when there's an actual search query
    // Check if data exists and is an array before searching
    // Only search if we have data - if data is not available, return empty arrays
    const filteredCalls = (callData && Array.isArray(callData) && callData.length > 0) ? searchCalls(callData, searchQuery) : [];
    // For wards, search orders from calls (booking orders)
    const filteredOrders = (!isKronk && callData && Array.isArray(callData) && callData.length > 0) ? searchOrders(callData, searchQuery) : [];
    // For kronk, search Shopify orders
    const filteredShopifyOrders = (isKronk && shopifyOrders && Array.isArray(shopifyOrders) && shopifyOrders.length > 0) ? searchShopifyOrders(shopifyOrders, searchQuery) : [];
    const filteredTickets = (tickets && Array.isArray(tickets) && tickets.length > 0) ? searchTickets(tickets, searchQuery) : [];
    const filteredInventory = (equipmentData && equipmentData.categories && Array.isArray(equipmentData.categories) && equipmentData.categories.length > 0 && isWardsCustomer()) ? searchInventory(equipmentData, searchQuery) : [];
    
    console.log('useSearchResults - Setting search results:', {
      searchQuery,
      customerId,
      callDataLength: callData?.length,
      ticketsLength: tickets?.length,
      shopifyOrdersLength: shopifyOrders?.length,
      filteredCallsLength: filteredCalls.length,
      filteredTicketsLength: filteredTickets.length,
      filteredOrdersLength: filteredOrders.length,
      filteredShopifyOrdersLength: filteredShopifyOrders.length,
      filteredCallsSample: filteredCalls.slice(0, 2),
      filteredTicketsSample: filteredTickets.slice(0, 2)
    });
    
    setSearchResults(prev => {
      const newResults = {
        calls: filteredCalls,
        orders: filteredOrders,
        shopifyOrders: filteredShopifyOrders,
        tickets: filteredTickets,
        inventory: filteredInventory
      };
      console.log('useSearchResults - State update:', {
        prevTicketsLength: prev.tickets.length,
        newTicketsLength: newResults.tickets.length,
        prevCallsLength: prev.calls.length,
        newCallsLength: newResults.calls.length,
        prevShopifyOrdersLength: prev.shopifyOrders.length,
        newShopifyOrdersLength: newResults.shopifyOrders.length
      });
      return newResults;
    });
  }, [searchQuery, callData, tickets, equipmentData, isWardsCustomer, shopifyOrders, getEffectiveCustomerId]);

  // Calculate total results
  const totalResults = searchQuery && searchQuery.trim() !== '' 
    ? searchResults.calls.length + searchResults.orders.length + searchResults.shopifyOrders.length + searchResults.tickets.length + searchResults.inventory.length
    : 0;
  
  // Get best matches (prioritize tickets, then orders, then calls/chat, then inventory)
  // Memoize to ensure it updates when searchResults changes
  const bestMatches = useMemo(() => {
    if (!searchQuery || searchQuery.trim() === '') return [];
    
    const customerId = getEffectiveCustomerId();
    const isKronk = customerId === 'kronk';
    const isWards = customerId === 'wards';
    
    // Combine orders: Shopify orders for kronk, booking orders for wards
    const allOrders = isKronk ? searchResults.shopifyOrders : searchResults.orders;
    
    // For calls/chat: filter out items that are already in orders or tickets
    // For kronk, we want chat (calls), for others we want calls
    const filteredCalls = searchResults.calls.filter(call => {
      // Exclude if it's already an order (for wards)
      if (!isKronk && searchResults.orders.some(order => order.id === call.id)) {
        return false;
      }
      // Exclude if it's already a ticket
      if (searchResults.tickets.some(ticket => ticket.id === call.id)) {
        return false;
      }
      return true;
    });
    
    return [
      ...searchResults.tickets.slice(0, 3),
      ...allOrders.slice(0, (isKronk || isWards) ? 1 : 0), // Show 1 order for kronk and wards
      ...filteredCalls.slice(0, 1), // Show 1 call/chat
      ...searchResults.inventory.slice(0, 0) // Don't show inventory in best match to keep it at 5
    ];
  }, [searchQuery, searchResults, getEffectiveCustomerId]);

  // Debug logging to track state changes
  // useEffect(() => {
  //   console.log('useSearchResults state update:', {
  //     searchQuery: searchQuery || '(empty)',
  //     searchQueryLength: searchQuery ? searchQuery.length : 0,
  //     callsCount: searchResults.calls.length,
  //     ordersCount: searchResults.orders.length,
  //     ticketsCount: searchResults.tickets.length,
  //     inventoryCount: searchResults.inventory.length,
  //     totalResults,
  //     hasCallData: !!callData,
  //     hasTickets: !!tickets,
  //     hasEquipmentData: !!equipmentData,
  //     isWardsCustomer: isWardsCustomer()
  //   });
  // }, [searchQuery, searchResults, totalResults, callData, tickets, equipmentData, isWardsCustomer]);
  
  // Return individual loading and error states for each data source
  // This allows tabs to handle errors independently
  const loading = callLoading || ticketsLoading || equipmentLoading || shopifyOrdersLoading;
  
  // Return individual error states so each tab can handle its own errors
  const errors = {
    calls: callError,
    orders: callError, // Orders use callData, so they share the same error
    shopifyOrders: shopifyOrdersError,
    tickets: ticketsError,
    inventory: equipmentError
  };

  // Only return a general error if ALL data sources fail
  // This prevents one failure from blocking all tabs
  const hasAnyError = callError || ticketsError || equipmentError || shopifyOrdersError;
  const generalError = hasAnyError ? {
    message: 'Some data sources failed to load. Individual tabs may show specific errors.',
    hasPartialData: true // Indicate that some data may still be available
  } : null;

  return {
    searchResults,
    totalResults,
    bestMatches,
    loading,
    error: generalError, // General error for backward compatibility
    errors // Individual errors for each data source
  };
};
